import { useQuery } from '@tanstack/react-query'
import type { OrgLoadState, OrgTreeRowView } from '@today/ui-domain'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type OrgUnitDTO, organizationService } from '@/services/organization.service'
import {
	ancestorsOf,
	buildTree,
	type FlatRow,
	filterTree,
	flattenTree,
	loadAllPages,
	type OrgTreeModel,
} from './org-tree'
import { ORG_ROW_HEIGHT, windowFor } from './org-window'

/** The query key of the whole unit list (every mutation that changes the tree invalidates it). */
export const ORG_UNITS_KEY = ['org-units'] as const
/** Tree viewport height in px (TO-389 organization-admin-ui REQ-004). */
export const ORG_VIEWPORT_HEIGHT = 640

const toRowView = (model: OrgTreeModel<OrgUnitDTO>, row: FlatRow): OrgTreeRowView => {
	const unit = model.byId.get(row.id) as OrgUnitDTO
	return {
		...row,
		code: unit.code,
		name: unit.name,
		typeCode: unit.typeCode,
		status: unit.status,
		descendantCount: model.descendantCount.get(row.id) ?? 0,
	}
}

const loadState = (isPending: boolean, isError: boolean, count: number): OrgLoadState => {
	if (isError) return 'error'
	if (isPending) return 'loading'
	return count === 0 ? 'empty' : 'ready'
}

/** Expanded ids: roots on first load, then the user's choices (kept across refetches). */
function useExpanded(model: OrgTreeModel<OrgUnitDTO>) {
	const [expanded, setExpanded] = useState<Set<string> | null>(null)
	const effective = useMemo(() => expanded ?? new Set(model.roots), [expanded, model])
	const toggle = (id: string) => {
		const next = new Set(effective)
		if (next.has(id)) next.delete(id)
		else next.add(id)
		setExpanded(next)
	}
	const expandAll = (ids: string[]) => {
		if (ids.every((id) => effective.has(id))) return
		setExpanded(new Set([...effective, ...ids]))
	}
	return { expanded: effective, toggle, expandAll }
}

/** Bring the selected unit into the tree once per selection or data change (expand its ancestors, then scroll). */
function useRevealSelected(
	model: OrgTreeModel<OrgUnitDTO>,
	rows: FlatRow[],
	unitId: string | null,
	reveal: { expandAll: (ids: string[]) => void; setScrollTop: (top: number) => void; scrollTop: number },
) {
	const revealed = useRef<string | null>(null)
	useEffect(() => {
		if (!unitId || !model.byId.has(unitId)) return
		reveal.expandAll(ancestorsOf(model, unitId))
		const index = rows.findIndex((r) => r.id === unitId)
		const key = `${unitId}|${model.byId.size}|${model.byId.get(unitId)?.parentId}`
		if (index < 0 || revealed.current === key) return
		revealed.current = key
		const top = index * ORG_ROW_HEIGHT
		if (top < reveal.scrollTop || top + ORG_ROW_HEIGHT > reveal.scrollTop + ORG_VIEWPORT_HEIGHT) {
			reveal.setScrollTop(Math.max(0, top - ORG_VIEWPORT_HEIGHT / 2))
		}
	})
}

/**
 * The tree tab's state (REQ-002/003/004): every unit loaded through the paged
 * list, the model, expand state, search filter, flattened rows and the virtual
 * window. The selected unit (from the URL) is revealed.
 */
export function useOrgTree({ enabled, unitId, query }: { enabled: boolean; unitId: string | null; query: string }) {
	const units = useQuery({
		queryKey: ORG_UNITS_KEY,
		queryFn: () => loadAllPages(organizationService.unitPage),
		enabled,
		staleTime: 30_000,
	})
	const model = useMemo(() => buildTree(units.data ?? []), [units.data])
	const { expanded, toggle, expandAll } = useExpanded(model)
	// The scroll offset belongs to one query: a new query starts at the top.
	const [scroll, setScroll] = useState({ top: 0, query })
	const scrollTop = scroll.query === query ? scroll.top : 0
	const setScrollTop = (top: number) => setScroll({ top, query })
	const filter = useMemo(() => filterTree(model, query), [model, query])
	const flat = useMemo(() => flattenTree(model, expanded, filter), [model, expanded, filter])
	useRevealSelected(model, flat, unitId, { expandAll, setScrollTop, scrollTop })
	return {
		state: loadState(units.isPending, units.isError, units.data?.length ?? 0),
		model,
		rows: useMemo(() => flat.map((row) => toRowView(model, row)), [flat, model]),
		window: windowFor({ scrollTop, viewportHeight: ORG_VIEWPORT_HEIGHT, rowCount: flat.length }),
		matchCount: filter ? filter.matches.size : null,
		scrollTop,
		setScrollTop,
		toggle,
		retry: () => void units.refetch(),
	}
}
